//
//  run-exporter.ts
//  Supernova CLI
//
//  Created by Jiri Trecak.
//  Copyright © Supernova.io. All rights reserved.
//

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports

import { Flags } from "@oclif/core"
import { SentryTraced } from "@sentry/nestjs"
import pulsarCoreTypes from "@supernova-studio/pulsar-core"

import pulsarLanguageTypes from "@supernova-studio/pulsar-language"
import axios from "axios"
import * as fs from "node:fs"
import { createRequire } from "node:module"
import path from "node:path"

import { z } from "zod"

import { SentryCommand } from "../types/index.js"
import { exportConfiguration } from "../utils/run-exporter/exporter-utils.js"
import { getWritableVersion } from "../utils/sdk.js"

const require = createRequire(import.meta.url)
const pulsarCoreProvider = require("@supernova-studio/pulsar-core") as typeof pulsarCoreTypes
const pulsarLanguageProvider = require("@supernova-studio/pulsar-language") as typeof pulsarLanguageTypes

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definition

interface RunLocalExporterFlags {
  allowOverridingOutput: boolean
  apiKey: string
  brandId?: string
  designSystemId: string
  exporterDir: string
  outputDir: string
  proxyUrl?: string
  themeId?: string[]
}

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Configuration

// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation

/** Command that describes the structure of provided design system */
export class RunLocalExporter extends SentryCommand<object> {
  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command configuration

  // How this command can be run
  static aliases = ["run-local-exporter"]
  // Command help description
  static description = "Run local exporter package"
  // Examples how to use the command
  static examples = [
    `$ @supernovaio/cli run-local-exporter \
      --apiKey="[API_KEY]" \
      --designSystemId="[DESIGN_SYSTEM_ID]" \
      --themeId="[THEME_ID]" \
      --brandId="[BRAND_ID]" \
      --exporterDir="[./path/to/exporter/dir]" \
      --outputDir="[./path/to/output/dir]" \
      --allowOverridingOutput
    `,
  ]
  // Static flags to enable / disable features
  static flags = {
    allowOverridingOutput: Flags.boolean({
      default: false,
      description:
        "When enabled, CLI will override output in the output directory if same files where present. When disabled, encountering the same file with throw an error.",
      required: false,
    }),
    apiKey: Flags.string({ description: "API key to use for accessing Supernova instance", required: true }),
    brandId: Flags.string({
      description:
        "Brand to export. Will only be used when exporter has usesBrands: true, but then it is required to be provided",
      required: false,
    }),
    designSystemId: Flags.string({ description: "Design System to export from", required: true }),
    exporterDir: Flags.string({ description: "Path to exporter package", required: true }),
    outputDir: Flags.string({
      description: "Path to output folder. Must be empty, unless `forceClearOutputDir` is set",
      required: true,
    }),
    proxyUrl: Flags.string({
      description: "When set, CLI will use provided proxy URL for all requests",
      hidden: true,
      required: false,
    }),
    themeId: Flags.string({
      delimiter: ",",
      description:
        "Themes to export. Accepts multiple values, either as a comma-separated list or by repeating the flag. Will only be used when exporter has usesThemes: true, and is optional",
      multiple: true,
      required: false,
    }),
  }

  get commandId(): string {
    return RunLocalExporter.id
  }

  get configSchema() {
    return z.object({})
  }

  // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
  // MARK: - Command runtime

  @SentryTraced()
  async executeExporter(
    flags: RunLocalExporterFlags,
    versionId: string,
  ): Promise<{
    logger: pulsarLanguageTypes.PLLogger
    result: Error | pulsarCoreTypes.PCEngineExporterProcessingResult
    success: boolean
  }> {
    // Log engine
    const logger = new pulsarLanguageProvider.PLLogger()

    // Initialize pulsar engine and export
    const pulsarEngine = new pulsarCoreProvider.PCPulsar(pulsarCoreTypes.PCPulsarExporterMode.full, logger)
    try {
      // Load pulsar with local URL
      await pulsarEngine.initiateWithLocalFolderURL(flags.exporterDir, pulsarCoreTypes.PCExporterEnvironment.ci)

      // Prepare configuration
      const config = exportConfiguration({
        apiKey: flags.apiKey,
        brandId: flags.brandId,
        dsId: flags.designSystemId,
        environment: this.env,
        exportPath: flags.exporterDir,
        logger,
        proxyUrl: flags.proxyUrl,
        themeIds: flags.themeId,
        versionId,
      })

      // Set logo overrides
      const result = await pulsarEngine.executeExporter(config, false)
      return {
        logger,
        result,
        success: true,
      }
    } catch (error) {
      // Return error
      return {
        logger,
        result: error as Error,
        success: false,
      }
    }
  }

  @SentryTraced()
  async run(): Promise<void> {
    const { flags } = await this.parse(RunLocalExporter)

    // Execute exporter
    // Get workspace -> design system –> version
    const { version } = await getWritableVersion(flags)
    const result = await this.executeExporter(flags, version.id)

    // Log user logs from the execution
    // Note this is currently not used because console.log is directly routed to the stdout and we have no control over it. Pulsar must be updated before this is doable
    if (flags.log) {
      this.logRun(result.logger)
    }

    if (result.success) {
      // Write result to output
      await this.writeToBuildPath(result.result as pulsarCoreTypes.PCEngineExporterProcessingResult, flags)
      this.log("Export finished successfully")
    } else {
      // Catch write error
      this.error(`Export failed: ${(result.result as Error).message}`, {
        code: "ERR_EXPORT_FAILED",
      })
    }
  }

  /** Download file into memory using Axios */
  @SentryTraced()
  private async downloadFileToMemory(fileUrl: string): Promise<Buffer> {
    const response = await axios({
      method: "get",
      responseType: "arraybuffer",
      url: fileUrl,
    })

    return Buffer.from(response.data)
  }

  /** Ensure directory exists - if it doesn't create it, recursively */
  @SentryTraced()
  private ensureDirectoryExists(filePath: string, forceLastFragmentIsDirectory: boolean = false) {
    // If the last fragment of path is forced, even paths without / are treated as dirs
    if (forceLastFragmentIsDirectory && !filePath.endsWith("/")) {
      filePath += "/"
    }

    // Only make directory if it doesn't exist
    if (fs.existsSync(filePath)) {
      return
    }

    fs.mkdirSync(filePath, { recursive: true })
  }

  /** Log run to output */
  private logRun(logger: pulsarLanguageTypes.PLLogger) {
    for (const log of logger.logs) {
      let message = log.message.trim()
      if (message.startsWith('"')) {
        message = message.slice(1)
      }

      if (message.endsWith('"')) {
        message = message.slice(0, Math.max(0, message.length - 1))
      }

      console.log(`[user]` + message)
    }
  }

  @SentryTraced()
  private async writeToBuildPath(
    result: pulsarCoreTypes.PCEngineExporterProcessingResult,
    flags: RunLocalExporterFlags,
  ) {
    // Create build directory if it doesn't exist, otherwise keep as it is. It should never be deleted!
    if (!fs.existsSync(flags.outputDir)) {
      this.ensureDirectoryExists(flags.outputDir)
    }

    // If overriding is disabled, test every possible file and if it exists, throw an error
    if (!flags.allowOverridingOutput) {
      for (const file of result.emittedFiles) {
        const destination = path.join(flags.outputDir, file.path)
        if (fs.existsSync(destination)) {
          throw new Error(
            `Exporter produced file for destination ${destination} but that file already exists. Enable --allowOverridingOutput option to allow overriding`,
          )
        }
      }
    }

    // Temporary structure to hold file contents
    const filesToWrite: { content: Buffer | string; filePath: string }[] = []

    // Function to process a single file
    const processFile = async (file: pulsarCoreTypes.PCEngineFileDescriptor) => {
      const filePath = path.join(flags.outputDir, ...file.path.split("/"))
      const fileDirectory = path.dirname(filePath)
      this.ensureDirectoryExists(fileDirectory)

      switch (file.type) {
        case "copy_file": {
          const fileContent = fs.readFileSync(file.content)
          filesToWrite.push({ content: fileContent, filePath })

          break
        }

        case "copy_file_remote": {
          const fileContent = await this.downloadFileToMemory(file.content)
          filesToWrite.push({ content: fileContent, filePath })

          break
        }

        case "string": {
          filesToWrite.push({ content: file.content, filePath })

          break
        }
        // No default
      }
    }

    // Process all files in chunks to speed up the process
    const chunkSize = 4
    for (let i = 0; i < result.emittedFiles.length; i += chunkSize) {
      const chunk = result.emittedFiles.slice(i, i + chunkSize)
      await Promise.all(chunk.map(file => processFile(file)))
    }

    // Write all files from the temporary structure to the filesystem as a final step - this is to avoid partial writes
    for (const { content, filePath } of filesToWrite) {
      fs.writeFileSync(filePath, content)
    }
  }
}
